/**
 * 표시 통화 환산 + 포맷 (FN-040 · PRD §3.2 · §5.7).
 *
 * 서버가 주는 시세는 **KRW 로 환산된 값**이다(적재 시점에 굳힌다). 그래서 표시 통화가 KRW 면
 * 환산이 없고, 다른 통화면 KRW → 그 통화로 한 번 더 환산한다.
 *
 * ⚠ 그래서 비KRW 표시는 **이중 환산**(원본 → KRW → 표시)이다. 국내 서비스 동안은 KRW 가 기본
 * 이라 실제로 거치는 경우가 드물지만, 글로벌 전환 때는 저장을 원본 통화로 바꿔 이 단계를
 * 없애는 것이 맞다 (2026-08-24 단계적 진행 결정).
 *
 * ⚠ **환율이 없으면 환산하지 않는다.** 통화를 골랐어도 환율을 못 받았으면 원래 통화로 적는다 —
 * 지어낸 환산가를 보여주는 것보다 낫다(PRD §7 정직성).
 */
import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  crossRate,
  exchangeRateUrl,
  fetchExchangeRates,
  type ExchangeRateSnapshot,
} from "../api/exchangeRate";
import { useCurrencyStore, DEFAULT_DISPLAY_CURRENCY } from "../store/currencyStore";
import { formatKrw } from "../lib/formatPrice";

export const EXCHANGE_RATE_KEY = ["exchange-rates"] as const;

/** 하루 1회 갱신되는 파일이라 6시간이면 충분히 신선하다. */
const STALE_MS = 6 * 60 * 60 * 1000;

export function useExchangeRatesQuery() {
  return useQuery<ExchangeRateSnapshot>({
    queryKey: EXCHANGE_RATE_KEY,
    queryFn: fetchExchangeRates,
    // 오리진이 없으면 아예 부르지 않는다 — 실패를 반복해 봐야 얻을 게 없다.
    enabled: exchangeRateUrl() !== null,
    staleTime: STALE_MS,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/** 통화별 표기 — 기호는 코드 앞에 붙이고, 원화만 한국 관용대로 '원' 을 뒤에 붙인다. */
const SYMBOL: Record<string, string> = {
  USD: "$", EUR: "€", JPY: "¥", GBP: "£", AUD: "A$", CAD: "C$", CNY: "CN¥", TWD: "NT$",
};

/** 소수 자릿수 — 원·엔은 소수를 쓰지 않는다. */
function fractionDigits(currency: string): number {
  return currency === "KRW" || currency === "JPY" ? 0 : 2;
}

export function formatIn(amount: number, currency: string): string {
  if (currency === "KRW") return formatKrw(amount);
  const digits = fractionDigits(currency);
  const body = amount.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  const symbol = SYMBOL[currency];
  return symbol ? `${symbol}${body}` : `${body} ${currency}`;
}

export interface MoneyFormatter {
  /** 지금 고른 표시 통화 */
  currency: string;
  /** 환율 기준일(YYYY-MM-DD). 환산이 실제로 일어날 때만 값이 있다 */
  rateBaseDate: string | null;
  /** 금액 한 건 — `sourceCurrency` 는 서버가 준 통화(기본 KRW) */
  format: (amount: number, sourceCurrency?: string) => string;
  /** 부호를 붙인 손익 표시. 0 은 부호 없이 (formatSignedKrw 와 같은 규칙) */
  formatSigned: (amount: number, sourceCurrency?: string) => string;
}

export function useMoney(): MoneyFormatter {
  const currency = useCurrencyStore((s) => s.currency);
  const { data } = useExchangeRatesQuery();

  const format = useCallback(
    (amount: number, sourceCurrency = DEFAULT_DISPLAY_CURRENCY) => {
      if (sourceCurrency === currency) return formatIn(amount, currency);
      const rate = crossRate(data, sourceCurrency, currency);
      // 환율을 모르면 원래 통화 그대로 — 없는 환산가를 지어내지 않는다
      if (rate === null) return formatIn(amount, sourceCurrency);
      return formatIn(amount * rate, currency);
    },
    [currency, data],
  );

  const formatSigned = useCallback(
    (amount: number, sourceCurrency = DEFAULT_DISPLAY_CURRENCY) => {
      const rounded = Math.round(amount);
      if (rounded === 0) return format(0, sourceCurrency);
      return `${rounded > 0 ? "+" : "-"}${format(Math.abs(amount), sourceCurrency)}`;
    },
    [format],
  );

  return {
    currency,
    rateBaseDate: currency === DEFAULT_DISPLAY_CURRENCY ? null : (data?.baseDate ?? null),
    format,
    formatSigned,
  };
}
